import { useState, useEffect } from "react"
import { Outlet, useLocation, useParams, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet";

import CLink from "../components/CLink.js";

import useArray from "../hooks/useArray.js";
import { useAuth } from "../contexts/AuthContext.jsx";
import axios from "../scripts/axiosConfig.js";


import "../styles/CodeHouse.scss";
import { toast } from "react-toastify"

type CodeLanguages = "C" | "CPP17" | "JAVA8" | "PYTHON3" | "JAVASCRIPT_NODE";

type CodeStorageType = {
    [problemID: string]: {
        [lang in CodeLanguages]: {
            "original": string,
            "user": string
        }
    };
};

type InitialCodeType = {
    language: CodeLanguages,
    code: string,
    _id: string
}

type ProblemType = {
    id: string,
    _id: string,
    title: string,
    testcaseLength: number,
    status: "attempted" | "not_attempted" | "solved",
    score: number,
    sampleInput: string,
    sampleOutput: string,
    problemScore: number,
    problemStatus: string,
    inputFormat: string,
    outputFormat: string,
    description: string,
    explanation: string,
    constraints: string,
    code?: string, // Optional property for storing code
    initialCode: InitialCodeType[]
}

type CodeType = "problem" | "contest" | "challenge";

export default function CodeHouse() {

    const { contestSlug } = useParams();

    const [codeType, setCodeType] = useState<CodeType>("problem");
    const [userStatus, setUserStatus] = useState("loading");
    const [pageToShow, setPageToShow] = useState("instructions");
    const [loadingPercentage, setLoadingPercentage] = useState(0);
    const [endTime, setEndTime] = useState(0);
    const [contestDuration, setContestDuration] = useState(0);
    const [openForever, setOpenForever] = useState(false);
    const [timedContest, setTimedContest] = useState(false);
    const [proctored, setProctored] = useState(false);
    const [pageTitle, setPageTitle] =  useState("");

    const { currentUser } = useAuth();

    const params = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const problemsList = useArray<ProblemType>([]);
    const problemsUserCode = useArray<any>([]);

    const [codeStorage, setCodeStorage] = useState<CodeStorageType>({});

    const goFullScreen = () => {
        document.documentElement.requestFullscreen();
    }

    const startRound = async () => {
        setPageToShow("loading");
        // goFullScreen();

        setLoadingPercentage(30);
        axios.post(`/contests/start/${contestSlug}`, { contest: contestSlug }, { headers: {Authorization: `Bearer ${await currentUser.getIdToken()}`} })
        .then((response) => {
            console.log(response);

            setLoadingPercentage(100);
            setContestDuration(response.data.contest.duration * 60);
            setEndTime(response.data.contest.endTime);
            setOpenForever(response.data.contest.openContestForever);
            setTimedContest(response.data.contest.timedContest);
            setProctored(response.data.contest.enableProctored);
            problemsList.setValue(response.data.problems);
            console.log("%c GETTING DATA ABOUT PROBLEMS", "color: red, font-weight: 600");
            console.log(`Contest Duration: ${response.data.contest.duration}`);
            console.log(response.data.problems);
            response.data.problems.map((problem: any, index: number) => {
                problemsUserCode.value[index] = problem.code;
            })
            setPageTitle(` ${response.data.contest.name} - Contest - Codify`)
            setPageToShow("code");

        })
        .catch((error) => {
            setPageToShow("instructions");
            console.log("catch")
            toast.error(error?.response?.data?.message || error?.response?.data?.error || "Something went wrong." );
            console.error("Error fetching data:", error);
            console.log("Error: ", error.response.data.message || error.response.data.error);
        });
    }


    useEffect(() => {
        console.log(`Current user: ${currentUser}`);
        if (currentUser || currentUser !== "none") {
            setUserStatus("approved");
        }
        else {
            setUserStatus("out");
        }
    }, [currentUser]);

    useEffect(() => {
        window.addEventListener('beforeunload', alertUser);

        console.log(">>>>>>>>>>> << >  > < > < >> >> > >>")
        const urlParts = location.pathname.slice(1).split("/")

        if (urlParts[0] === "code") {
            console.log("Coming from code.")
            // just code, challenge may or may not be present as a query parameter.
            // do not show instructions screen. skip to code screen.
            const challengeID = searchParams.get("challenge");
            if (challengeID) {
                setCodeType("challenge");
                // get other problems in the challenge and show them in the sidebar. get Name from DB.
                console.log("Part of a challenge.")
                setPageTitle(`Code - ${challengeID} - Codify`);

                axios.post("/problem/get", { problemID: params.problemID, challengeID })
                .then((response) => {
                    problemsList.setValue(response.data.problems);
                    setPageToShow("code");
                    setPageTitle(`${response.data.challenge.name} - Challenge - Codify`);

                })
                .catch((error) => {
                    setPageToShow("instructions");
                    toast.error(error?.response?.data?.message || error?.response?.data?.error || "Something went wrong." );
                    console.log(`Error while fetching ${codeType} details: `, error.response.data.message || error.response.data.error);
                });
            }
            else {
                setCodeType("problem");
                // single problem. dont show problems sidebar.
                // get problem name from problemID.
                setPageTitle(`${params.problemID}  - Solve - Codify`)
                
                axios.post("/problem/get", { problemID: params.problemID })
                .then((response) => {
                    problemsList.setValue(response.data.problems);
                    setPageToShow("code");
                    setPageTitle(`${response.data.problems[0]?.title || params.problemID} - Problem - Codify`);
                })
                .catch((error) => {
                    setPageToShow("instructions");
                    toast.error(error?.response?.data?.message || error?.response?.data?.error || "Something went wrong." );
                    console.log(`Error while fetching ${codeType} details: `, error.response.data.message || error.response.data.error);
                });
            }
            setLoadingPercentage(100);

        }
        else if (urlParts[0] === "contests") {
            console.log("Coming from contest.")
            setCodeType("contest");
            // user is attempting a contest. prepare for proctored mode.
            // unlike ^^ code, single problem ID will not be present, from the contest ID, use old gfg mechanisms
            // and get all problems in the contest.
            // show instructions screen.
            setPageTitle(`Contest - Codify`)
            // setProblemData(re)
        }

        return () => {
          window.removeEventListener('beforeunload', alertUser);
        }
      }, [])

    useEffect(() => {
        if (pageToShow !== "code") {
            console.log("removing alertUser");
            window.removeEventListener('beforeunload', alertUser);
        }
        else {
            console.log("adding alertUser");
            window.addEventListener('beforeunload', alertUser);
        }
    }, [pageToShow])

      const alertUser = (e: any) => {
        e.preventDefault();
        e.returnValue = '';
      }

    const finishRound = async (finishTime: number) => {
        axios.post(
            `/contests/finish`, 
            { contestSlug: contestSlug, time: finishTime }, 
            { headers: {Authorization: `Bearer ${await currentUser.getIdToken()}`} }
        )
        .then((response) => {   
            console.log(response);
            document.exitFullscreen();
            window.removeEventListener('beforeunload', alertUser);
            console.log("set to true");
            setPageToShow("finished");
        })
        .catch((error) => {
            toast.error(error?.response?.data?.message || error?.response?.data?.error || "Something went wrong." );
            console.error("Error fetching data:", error);
        });
    }

      const saveEditorCodeLocally = (problemIndex: number, editorCode: string) => {
        console.log("CodeHouse: setting code locally...");
        console.log(editorCode);
        problemsList.value[problemIndex].code = editorCode;
        
      }


    return (
        <>

        <Helmet>
            <title>{ pageTitle }</title>
        </Helmet>

        <div className="codeHouse">
            {
                (userStatus === "approved") ? (
                    <>
                        {
                            (pageToShow === 'instructions' || pageToShow === 'loading') ? (
                                <>
                                    {
                                        (pageToShow === "loading") ? (
                                            <div className="progressBar">
                                                <div className="progressBar-thumb" style={{ width: `${loadingPercentage}%` }}></div>
                                            </div>
                                        ) : (
                                            <></>
                                        )
                                    }
                                
                                    <div className="instructions">
                                        <div className="title">READ ME!</div>
                                        <div className="points">
                                            <ol>
                                                <li>This is a proctored contest.</li>
                                                <li>Do not exit from the page.</li>
                                                <li>Do not switch tabs.</li>
                                                <li>Copy and Pasting code is disabled.</li>
                                                <li>Do not try to login and participate in two devices at the same time.</li>
                                                <li>But most importantly, be calm and have fun! :)</li>

                                            </ol>
                                        </div>
                                        <button className="startRoundBtn" onClick={startRound} disabled={pageToShow === "loading"}>
                                            {
                                                (pageToShow === "instructions") ? "Start" : "..."
                                            }
                                        </button>
                                    </div>

                                </>
                            ) : (
                                <></>
                            )
                        }

                        {
                            (pageToShow === 'loggedout') ? (
                                    <div className="instructions">
                                        <div className="title">You have logged out!</div>
                                        <div className="points">
                                            You have logged out from the page. You can continue the contest in another device.
                                        </div>
                                    </div>
                            ) : (
                                <></>
                            )
                        }

                        
                        {
                            (pageToShow === 'ended') ? (
                                        <div className="instructions">
                                            <div className="title">The contest has ended!</div>
                                            <div className="points">
                                                Thank you for participating. We hope you had fun! Results will be released shortly.
                                            </div>
                                        </div>
                                ) : (
                                    <></>
                                )
                        }

                        {
                            (pageToShow === 'finished') ? (
                                        <div className="instructions">
                                            <div className="title">You have finished the contest!</div>
                                            <div className="points">
                                                Thank you for participating. We hope you had fun! Results will be released shortly.
                                            </div>
                                        </div>
                                ) : (
                                    <></>
                                )
                        }


                        {
                            (pageToShow === 'malpractice') ? (
                                        <div className="instructions">
                                            <div className="title">You have been eliminated!</div>
                                            <div className="points">
                                                You have been eliminated from the contest. Contact the event coordinators.
                                            </div>
                                        </div>
                                ) : (
                                    <></>
                                )
                        }

                        
                    
                        {
                            (pageToShow === "code") ? (
                                <Outlet context={{
                                    type: codeType,
                                    // problemData,
                                    challengeProblems: null,
                                    contestSlug, 
                                    setPageToShow, 
                                    problemsList,
                                    problemsUserCode,
                                    codeStorage,
                                    setCodeStorage,
                                    timedContest,
                                    contestDuration, 
                                    openForever,
                                    endTime,
                                    proctored,
                                    saveEditorCodeLocally, 
                                    finishRound
                                }} />
                            ) : (
                                <></>
                            )
                        }
                    </>
                ) : (
                    <></>
                )
            }
            {
                (userStatus === "rejected") ? (
                    <div className="rejected">
                        <div className="title">Sorry!</div>
                        <div className="points">
                            <ul>
                                <li>Your email ({currentUser.email}) is not allowed to participate in this event.</li>
                                <li>Kindly contact the event organizers for more details.</li>
                                <li>
                                    <CLink to={"/profile"}>Sign in with another email?</CLink>
                                    {/* im tired. 'a' not getting margin even if i mention in css, wrapping in li to inherit li properties to get some spacing. */}
                                </li>
                            </ul>
                        </div>
                    </div>
                ) : (
                    <></>
                )
            }
            {
                (userStatus === "out") ? (
                    <div className="out">
                        <div className="title">You are not logged in.</div>
                        <div>
                            You are not logged in. Please <CLink to={"/profile"}>Sign in</CLink> to continue.
                        </div>
                    </div>
                ) : (
                    <></>
                )
            }
            {
                (userStatus === "loading") ? (
                    <div className="loading">
                        <div className="title">Please wait...</div>
                        <div>
                            Your account status is being verified. Please wait.
                        </div>
                    </div>
                ) : (
                    <></>
                )
            }
        </div>
        
        </>


    )
}